import { useEffect, useState } from "react";
import { FileWarning, Loader2, Lock, Gavel } from "lucide-react";
import api from "@/lib/api";
import { DEMO_WARRANTS } from "./mdtDemo";
import { btnCls, toastOk } from "./RosterTab";

/* Active warrant board — issued from NCIC hits, cleared once the suspect is processed.
   Serious warrants freeze the suspect's bank until cleared. */

export default function WarrantsTab({ G }) {
  const { serverId, token, demo, sfx } = G;
  const [warrants, setWarrants] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => {
    if (demo) { setWarrants(DEMO_WARRANTS); return; }
    api.get(`/nc/servers/${serverId}/cad/warrants`, { params: { token } })
      .then((r) => setWarrants(r.data.warrants)).catch(() => setWarrants([]));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const clear = async (w) => {
    sfx.keyTick();
    if (demo) { setWarrants((x) => x.filter((y) => y.id !== w.id)); return; }
    setBusy(w.id);
    try { const r = await api.post(`/nc/servers/${serverId}/cad/warrants/${w.id}/clear`, {}, { params: { token } }); toastOk(r.data.message); load(); }
    catch { /* next poll */ } finally { setBusy(""); }
  };

  if (!warrants) return <div className="flex justify-center p-8"><Loader2 className="w-4 h-4 animate-spin text-sky-400" /></div>;

  return (
    <div className="p-3.5 flex flex-col gap-2" data-testid="warrants-tab">
      <p className="font-mono text-[8px] uppercase tracking-[0.25em] text-zinc-500 flex items-center gap-1.5">
        <FileWarning className="w-3 h-3 text-red-400" /> Active warrants — issue them from an NCIC hit on the Board view
      </p>
      {warrants.map((w) => (
        <div key={w.id} data-testid={`warrant-${w.id.slice(0, 6)}`} className="border border-red-500/30 bg-red-500/[0.06] p-3 flex flex-col gap-1.5">
          <div className="flex items-center gap-2 flex-wrap">
            <Gavel className="w-3.5 h-3.5 text-red-400" />
            <p className="font-mono text-[11px] font-bold text-red-300">{w.name} <span className="text-zinc-500">#{w.playercode}</span></p>
            <span className="font-mono text-[8px] uppercase text-red-300 border border-red-500/50 bg-red-500/15 px-1.5 py-0.5 animate-pulse">Wanted</span>
            {w.funds_locked && <span className="font-mono text-[8px] uppercase text-amber-300 border border-amber-500/40 px-1.5 py-0.5 flex items-center gap-1"><Lock className="w-2.5 h-2.5" /> Funds locked</span>}
          </div>
          <p className="font-mono text-[9.5px] text-zinc-300 leading-snug">{w.reason}</p>
          <div className="flex items-center gap-2 font-mono text-[8px] text-zinc-500 uppercase">
            <span>issued by {w.issued_by} · {String(w.at).slice(0, 16).replace("T", " ")}</span>
            {w.signed_by && <span data-testid={`warrant-signed-${w.id.slice(0, 6)}`} className="text-nc-green border border-nc-green/40 px-1.5 py-0.5">Signed · Judge {w.signed_by}</span>}
            {!w.signed_by && <span className="text-zinc-600">awaiting bench signature</span>}
            <button data-testid={`warrant-clear-${w.id.slice(0, 6)}`} disabled={busy === w.id} onClick={() => clear(w)}
              className={`${btnCls} ml-auto border-white/20 text-zinc-300 hover:border-emerald-400 hover:text-emerald-300`}>
              {busy === w.id ? "…" : "Clear warrant"}{w.funds_locked ? " & release funds" : ""}
            </button>
          </div>
        </div>
      ))}
      {warrants.length === 0 && <p className="font-mono text-[9px] text-zinc-500 uppercase tracking-[0.2em] p-4 text-center">No active warrants — city is clean</p>}
    </div>
  );
}
